<template>
  <div class="reorderStoreList">
    <label class="listLabel" for="reorder-store-search">{{ t('reorder.storeList.title') }}</label>
    <div class="searchWrapper">
      <input
        id="reorder-store-search"
        v-model="searchQuery"
        type="text"
        :placeholder="t('reorder.storeList.searchPlaceholder')"
        autocomplete="off"
      />
      <svg class="searchIcon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <circle cx="7" cy="7" r="5.25" stroke="currentColor" stroke-width="1.5" />
        <path d="M11 11L14.5 14.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
      </svg>
    </div>
    <div class="sortRow">
      <div class="sortWrapper" ref="sortWrapper">
        <button
          type="button"
          class="sortButton"
          :aria-expanded="sortMenuIsOpen"
          aria-haspopup="listbox"
          @click="sortMenuIsOpen = !sortMenuIsOpen"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M5 2.5V13.5M5 13.5L2.5 11M5 13.5L7.5 11M11 13.5V2.5M11 2.5L8.5 5M11 2.5L13.5 5"
              stroke="currentColor"
              stroke-width="1.3"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          {{ selectedSortLabel }}
        </button>
        <div v-if="sortMenuIsOpen" class="sortMenu" role="listbox" :aria-label="t('reorder.storeList.sortAria')">
          <button
            v-for="option in sortOptions"
            :key="option.value"
            type="button"
            role="option"
            class="sortOption"
            :class="{ active: sortBy === option.value }"
            :aria-selected="sortBy === option.value"
            @click="selectSort(option.value)"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
    </div>
    <p class="listHint" id="reorder-store-list-label">{{ t('reorder.storeList.hint') }}</p>
    <div v-if="loading" class="loaderWrapper">
      <div class="loader"></div>
    </div>
    <p v-else-if="errorKey" class="feedback error">{{ t(errorKey) }}</p>
    <p v-else-if="!sortedStores.length" class="emptySearch">
      {{ t('reorder.storeList.noMatch', { search: searchQuery }) }}
    </p>
    <div v-else class="stores" role="listbox" aria-labelledby="reorder-store-list-label">
      <button
        v-for="store in sortedStores"
        :key="store.store_id"
        type="button"
        role="option"
        class="storeCard"
        :class="{ selected: store.store_id == pickedStoreId }"
        :aria-selected="store.store_id == pickedStoreId"
        :disabled="disabled || !canStartOrder(store)"
        @click="pick(store)"
      >
        <div class="storeHeader">
          <p class="storeName">{{ store.name }}</p>
          <span class="status" :class="isAvailable(store) ? 'available' : 'closed'">
            {{ isAvailable(store) ? t('reorder.storeList.available') : t('reorder.storeList.closed') }}
          </span>
          <svg
            v-if="isFavorite(store)"
            class="favorite"
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="16"
            viewBox="0 0 30 25.832"
            role="img"
            :aria-label="t('reorder.storeList.favoriteAria')"
          >
            <path
              d="M46.654,0A7.425,7.425,0,0,0,39.83,4.53,7.422,7.422,0,0,0,33.006,0C27.9,0,24.83,4.864,24.83,8.874c0,6.719,15,16.959,15,16.959s15-10.238,15-16.958C54.83,4.864,51.758,0,46.654,0Z"
              transform="translate(-24.83)"
            />
          </svg>
        </div>
        <p v-if="!branding.display_options?.hide_store_address" class="storeAddress">
          {{ store.address1 }}, {{ store.city }}, {{ store.state }} {{ store.postal_code }}
        </p>
        <div class="storeMeta">
          <p v-if="getHours(store)">{{ getHours(store) }}</p>
          <p v-if="store.distance !== null">{{ t('reorder.storeList.distance', { distance: store.distance }) }}</p>
        </div>
      </button>
    </div>
  </div>
</template>

<script>
import { displayTime } from '@/utils/dateFormat'
import { isStoreAvailableAt, getStoreHoursForDay } from '@/utils/storeAvailability'

import { mapState, mapMutations } from 'vuex'
import { useI18n } from 'vue-i18n'

import customersApi from '@/api/customersApi'
import storesApi from '@/api/storesApi'

export default {
  props: {
    serviceTypeId: {
      type: [Number, String],
      required: true,
    },
    requiresAddress: {
      type: Boolean,
      default: false,
    },
    // delivery address; distances are measured from it, else from the current store
    address: {
      type: Object,
      default: null,
    },
    originStore: {
      type: Object,
      default: null,
    },
    selectedStoreId: {
      type: [Number, String],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // the order time picked in the modal (see utils/storeAvailability); availability and hours follow it
    orderTime: {
      type: Object,
      default: null,
    },
  },
  emits: ['select'],
  setup() {
    const { t } = useI18n()
    return { t }
  },
  data() {
    return {
      // highlighted right on click, not only once the parent has processed the pick
      pickedStoreId: this.selectedStoreId,
      // last known order time: the slots reload (orderTime null) for a moment after a store is picked
      currentOrderTime: this.orderTime,
      stores: [],
      favoriteStoreIds: [],
      loading: true,
      // i18n key, so the message follows a language change
      errorKey: null,
      searchQuery: '',
      // 'default' = available first (the AC's default order); the rest come from Sort by
      sortBy: 'default',
      sortMenuIsOpen: false,
    }
  },
  computed: {
    ...mapState(['branding', 'loggedIn']),
    sortOptions() {
      return [
        { value: 'nearby', label: this.t('reorder.storeList.sortNearby') },
        { value: 'nameAsc', label: this.t('reorder.storeList.sortNameAsc') },
        { value: 'nameDesc', label: this.t('reorder.storeList.sortNameDesc') },
      ]
    },
    selectedSortLabel() {
      return (
        this.sortOptions.find((option) => option.value === this.sortBy)?.label || this.t('reorder.storeList.sortBy')
      )
    },
    filteredStores() {
      const query = this.searchQuery.trim().toLowerCase()
      if (!query) return this.stores
      return this.stores.filter((store) =>
        [store.name, store.address1, store.city, store.state, store.postal_code]
          .filter(Boolean)
          .some((field) => String(field).toLowerCase().includes(query))
      )
    },
    sortedStores() {
      const byDistance = (a, b) => {
        if (a.distance === b.distance) return 0
        if (a.distance === null) return 1
        if (b.distance === null) return -1
        return a.distance - b.distance
      }
      return this.filteredStores.slice().sort((a, b) => {
        if (this.sortBy === 'nameAsc') return a.name.localeCompare(b.name)
        if (this.sortBy === 'nameDesc') return b.name.localeCompare(a.name)
        if (this.sortBy === 'nearby') return byDistance(a, b) || a.name.localeCompare(b.name)
        // default: available first, then favorites, then the closest
        const availability = Number(this.isAvailable(b)) - Number(this.isAvailable(a))
        if (availability !== 0) return availability
        const favorite = Number(this.isFavorite(b)) - Number(this.isFavorite(a))
        if (favorite !== 0) return favorite
        return byDistance(a, b) || a.name.localeCompare(b.name)
      })
    },
  },
  watch: {
    selectedStoreId(storeId) {
      this.pickedStoreId = storeId
    },
    orderTime(orderTime) {
      if (orderTime) this.currentOrderTime = orderTime
      // TEMP(reorder): remove after QA
      const sample = this.stores[0]
      console.log('[reorder] list orderTime:', JSON.stringify(orderTime), '| first store:', sample?.name,
        '| is_open:', sample?.is_open, '| schedule dates:', Object.keys(sample?.service_schedule?.[this.serviceTypeId] || {}),
        '| has hours:', Array.isArray(sample?.hours))
    },
  },
  mounted() {
    this.handleSortClickOutside = (event) => {
      if (this.sortMenuIsOpen && !this.$refs.sortWrapper?.contains(event.target)) {
        this.sortMenuIsOpen = false
      }
    }
    document.addEventListener('click', this.handleSortClickOutside)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleSortClickOutside)
  },
  methods: {
    ...mapMutations(['SET_COOKIES']),
    selectSort(value) {
      this.sortBy = value
      this.sortMenuIsOpen = false
    },
    pick(store) {
      if (!this.canStartOrder(store)) return
      this.pickedStoreId = store.store_id
      this.$emit('select', store)
    },
    getStoreService(store) {
      return (store?.services || []).find((service) => service.service_type_id == this.serviceTypeId) || null
    },
    // Open now for ASAP; for a future order, open at the picked day and time.
    isAvailable(store) {
      return isStoreAvailableAt(store, this.serviceTypeId, this.currentOrderTime)
    },
    // Reorder location change: only stores available for the order time can be picked.
    // Closed cards stay visible so the guest can see why they cannot switch there.
    canStartOrder(store) {
      return this.isAvailable(store)
    },
    isFavorite(store) {
      return this.favoriteStoreIds.includes(String(store.store_id))
    },
    getHours(store) {
      return getStoreHoursForDay(store, this.serviceTypeId, this.currentOrderTime, displayTime)
    },
    getDistance(lat1, lon1, lat2, lon2) {
      const toRad = (deg) => deg * (Math.PI / 180)
      const R = 6371 // km
      const dLat = toRad(lat2 - lat1)
      const dLon = toRad(lon2 - lon1)
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
      const km = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      return Number((km * 0.621371).toFixed(1))
    },
    getOrigin() {
      if (this.address?.latitude && this.address?.longitude) {
        return { lat: this.address.latitude, lng: this.address.longitude }
      }
      if (this.originStore?.latitude && this.originStore?.longitude) {
        return { lat: this.originStore.latitude, lng: this.originStore.longitude }
      }
      return null
    },
    async getFavoriteStores() {
      if (!this.loggedIn) return
      try {
        const response = await customersApi.getFavoriteStores(localStorage.getItem('customerId'))
        this.favoriteStoreIds = (response.data.data || []).map((location) => String(location.store_id))
        this.SET_COOKIES(response.data.cookies)
      } catch (error) {
        console.log(error)
      }
    },
    async getStores() {
      const payload = {
        all_stores: true,
        service_schedule: true,
        service_type_id: this.serviceTypeId,
      }
      // delivery: only the stores that deliver to the order's address
      if (this.requiresAddress) {
        delete payload.all_stores
      }
      if (this.address?.latitude && this.address?.longitude) {
        payload.latitude = this.address.latitude
        payload.longitude = this.address.longitude
      }
      try {
        const response = await storesApi.getStores(payload)
        const origin = this.getOrigin()
        this.stores = (response.data.data || [])
          // the reorder keeps its service type, so only stores offering it; inactive stores are
          // browse-only on HomeView and can't take the order either
          .filter((store) => store.active && this.getStoreService(store))
          .map((store) => ({
            ...store,
            distance:
              origin && store.latitude && store.longitude
                ? this.getDistance(origin.lat, origin.lng, store.latitude, store.longitude)
                : null,
          }))
        this.SET_COOKIES(response.data.cookies)
        if (!this.stores.length) {
          this.errorKey = 'reorder.storeList.noOtherLocations'
        }
      } catch (error) {
        console.log(error)
        this.errorKey = this.requiresAddress ? 'homeview.noLocationsDelivering' : 'reorder.storeList.noLocationsNow'
      }
    },
  },
  async created() {
    await Promise.all([this.getFavoriteStores(), this.getStores()])
    this.loading = false
  },
}
</script>

<style src="../../assets/css/Elements/ReorderStoreList.css" scoped></style>
